/**
 * Whether the V3 owner shell renders for this session (COL-528).
 *
 * The PRD's rollout: "ship behind one flag, off by default, and enable it for
 * admin impersonation first. Owners keep the current layout until every
 * sidebar section works."
 *
 * So there are two ways in, and they are deliberately different in kind:
 *
 *   VITE_OWNER_PORTAL_V3   the real switch. Unset means OFF everywhere,
 *                          development included — the shell lands before the
 *                          pages it frames, and a half-built sidebar is worse
 *                          for local work than the strip that already works.
 *   impersonation          an admin ACTING AS an owner gets V3 even with the
 *                          flag unset, so staff can review it against real
 *                          portfolios while owners still get V2. Keyed on
 *                          `impersonatedUser.kind`, which is the only thing
 *                          that actually changes when an admin acts as an
 *                          owner — see the note on the return below.
 *
 * An explicit `VITE_OWNER_PORTAL_V3=false` beats both. Without that escape
 * hatch an admin could never see what an owner currently sees, which is the
 * thing they are most often asked about.
 *
 * ONE HOOK, read everywhere. The shell, the property cards, the analytics page
 * and the detail chrome each decide something on this answer; if they read it
 * differently, an owner gets a V3 sidebar around V2 cards.
 */
import { useAuth } from "@/contexts/AuthContext";
import {
  isOwnerPortalV3Enabled,
  isOwnerPortalV3ForcedOff,
} from "@/lib/featureFlags";

export function useOwnerPortalV3(): boolean {
  const { impersonatedUser } = useAuth();
  if (isOwnerPortalV3ForcedOff()) return false;
  if (isOwnerPortalV3Enabled()) return true;
  // Acting as an OWNER specifically — an admin in their own console, or acting
  // as a tenant, is looking at screens this shell has nothing to do with.
  //
  // This reads impersonatedUser.kind and NOT isOwnerPortalOnly, which is what
  // it used to read and was unsatisfiable: isOwnerOnly() is
  // `roles.includes("owner") && !hasStaffRole(roles)`, and impersonate() never
  // swaps `roles` — an admin keeps their staff roles for the whole session. So
  // `isOwnerPortalOnly && isImpersonating` could never both be true, and the
  // branch meant for staff review was dead code. kind is set by
  // impersonate(id, "owner") and is the only signal that actually flips.
  return impersonatedUser?.kind === "owner";
}
